<template>
	<div id="top-header" :class="!shouldDisplayNavComponents ? 'scrollable' : ''">
		<div>
			<div class="breadcrumb-path-link">
				<a :href="link">Investments</a>
				<span v-if="fundNameTitleString" class="fund-title" v-html="fundNameTitleString" />
				<span v-else class="fund-title">
					/ {{ fundNameLegal }}
					<sup>{{ trademarkLegal }}</sup>
				</span>
			</div>
			<div id="page-banner">
				<div class="title-container">
					<div class="title">
						<div>{{ fundGroupDescription }}</div>
						<h2 :class="{ 'bottom-spacer': !shouldShowJumpToFundButton }">
							<slot v-if="$slots.title" name="title" />
							<template v-else>
								<p v-html="title" />
								<p v-if="fundSymbol">({{ fundSymbol }})</p>
							</template>
						</h2>
					</div>
					<button v-if="shouldShowJumpToFundButton" class="modal-link" @click="emitOpenModal">
						{{ fundSelectionButtonText }}
					</button>
				</div>
			</div>
		</div>
		<div v-if="shouldDisplayNavComponents" id="toolbar-wrapper">
			<div id="tool-bar">
				<div class="share-class-dropdown">
					<p v-if="isInstitutional" class="table-label vehicle">Vehicle:</p>
					<p v-if="isInstitutional" class="table-label vehicle-value">Mutual Fund</p>
					<p class="table-label">
						Share Class
						<slot name="Iball_shareclass" />
					</p>
					<select id="page-shareclass-select" @change="reloadPage($event)" v-model="currentShareClass">
						<option v-for="className in availableShareClassList" :key="className" :value="className">
							{{ className }}
						</option>
					</select>
				</div>
				<i class="icon icon-plus" />
				<button id="download-data" class="item" @click="emitDownload">DOWNLOAD DATA</button>
				<i v-if="false" class="icon icon-print" />
				<a v-if="false" class="item print" @click="print">PRINT</a>
			</div>
		</div>
		<div v-if="shouldDisplayNavComponents" id="nav-wrapper">
			<div id="nav-bar">
				<button v-for="section in navSections" :key="section.label"
					:class="activeNavTab === section.label ? 'active' : ''" @click="scrollToSection(section.elementId)">
					{{ section.label }}
				</button>
			</div>
		</div>
		<div v-if="shouldDisplayNavComponents && shouldRenderDropDown" id="nav-dropdown">
			<div class="selected" @click="toggleNavDropdown">
				{{ activeNavTab }}
				<slot v-if="activeNavTab.toLowerCase() === 'holdings'" name="superscript1" />
				<slot v-if="activeNavTab.toLowerCase() === 'fees & expenses'" name="superscript2" />
				<div class="nav-dropdown-arrow" :class="navDropdownIsOpen ? 'up' : 'down'" />
			</div>
			<template v-for="section in navSections">
				<div v-if="(section.label !== activeNavTab) && navDropdownIsOpen" :key="section.label"
					@click="() => updateSelection(section.label, true)">
					{{ section.label }}
				</div>
			</template>
		</div>
	</div>
</template>

<script>
const breakpoint = 768;
export default {
	name: 'PageBanner',
	props: ['widgetData'],
	data() {
		return {
			previousWindowWidth: window.innerWidth,
			navDropdownIsOpen: false,
			domUpdateWatcher: null,
			shouldRenderDropDown: true
		};
	},
	computed: {
		componentData() {
			return this.widgetData || {};
		},
		audience() {
			return this.componentData.audience || '';
		},
		isInstitutional() {
			return this.audience === 'institutional';
		},
		fundNameLegal() {
			return this.componentData.fundNameLegal || '';
		},
		//this is a formatted title for AFIS targetdate objectives to have the trademark after amreican funds, not at the end
		fundNameTitleString() {
			return this.componentData.fundNameTitleString;
		},
		trademarkLegal() {
			return this.componentData.trademarkLegal || '';
		},
		fundSymbol() {
			return this.componentData.fundSymbol || null;
		},
		title() {
			return this.componentData.title || '';
		},
		link() {
			return this.componentData.link || '';
		},
		fundGroupDescription() {
			return this.componentData.fundGroupDescription || '';
		},
		shouldShowJumpToFundButton() {
			return !!this.fundSelectionButtonText;
		},
		fundSelectionButtonText() {
			return this.componentData.fundSelectionButtonText;
		},
		availableShareClassList() {
			return this.componentData.availableShareClassList;
		},
		currentShareClass() {
			return this.componentData.currentShareClass || '';
		},
		navSections() {
			return this.componentData.navSections || [];
		},
		activeNavTab() {
			return this.componentData.activeNavTab || '';
		},
		shouldDisplayNavComponents() {
			if (!this.componentData.shouldDisplayNavComponents) {
				window.removeEventListener('scroll', this.stickyScroll);
			}
			return this.componentData.shouldDisplayNavComponents;
		},
		//detect if position sticky is supported to see if we need a manual solution (IE11 and lower)
		//http://trialstravails.blogspot.com/2016/06/detecting-css-position-sticky-support.html?_sm_au_=iHVKQn7GFVFttrs6
		stickySupported() {
			const el = document.createElement('a');
			const mStyle = el.style;
			mStyle.cssText =
				'position:sticky;position:-webkit-sticky;position:-ms-sticky;';
			return mStyle.position.indexOf('sticky') !== -1;
		}
	},
	beforeMount() {
		if (window.innerWidth > breakpoint) {
			this.shouldRenderDropDown = false;
			window.addEventListener('scroll', this.stickyScroll);
		}
		window.addEventListener('resize', this.eventResize);
	},
	methods: {
		emitDownload() {
			this.$emit('download-data', {});
		},
		emitOpenModal() {
			this.$emit('openModal', 'jump modal');
		},
		updateSelection(section, shouldToggle) {
			this.$emit('changeTab', section);
			if (shouldToggle) this.toggleNavDropdown();
		},
		toggleNavDropdown() {
			this.navDropdownIsOpen = !this.navDropdownIsOpen;
		},
		reloadPage(event) {
			const shareClass =
				(event && event.target && event.target.value) || '';
			this.$emit('reloadPage', shareClass);
		},
		print() {
			this.$emit('printToPdf');
		},
		eventResize() {
			if (
				this.previousWindowWidth <= breakpoint &&
				window.innerWidth > breakpoint
			) {
				this.previousWindowWidth = window.innerWidth;
				this.shouldRenderDropDown = false;
				const section = this.navSections.filter(
					item => item.label === this.activeNavTab
				);
				const elementId = section && section[0] && section[0].elementId;
				const checkForElementsExistance = this
					.checkForElementsExistance;
				this.domUpdateWatcher = new MutationObserver(() => {
					checkForElementsExistance(elementId);
				});
				const doc = document;
				this.domUpdateWatcher.observe(doc, {
					childList: true,
					subtree: true
				});
			} else if (
				this.previousWindowWidth > breakpoint &&
				window.innerWidth <= breakpoint
			) {
				this.shouldRenderDropDown = true;
				this.previousWindowWidth = window.innerWidth;
				const headerElementId = 'top-header';
				this.scrollToSection(headerElementId, 'auto');
			}
			if (window.innerWidth > breakpoint) {
				window.addEventListener('scroll', this.stickyScroll);
			} else {
				window.removeEventListener('scroll', this.stickyScroll);
			}
		},
		checkForElementsExistance(elementId) {
			const element = document.getElementById(elementId);
			if (element) {
				this.scrollToSection(elementId, 'auto');
				this.domUpdateWatcher.disconnect();
				this.domUpdateWatcher = null;
			}
		},
		scrollToSection(sectionId, scrollBehavior) {
			const topHeader = document.getElementById('top-header');
			const topHeaderHeight =
				topHeader &&
				topHeader.getBoundingClientRect() &&
				topHeader.getBoundingClientRect().height;

			//If we're integrated the client bar and header height need to be added to the scroll value
			const clientBar = document.getElementsByClassName('new-navigation');
			const clientBarHeight = clientBar.length
				? clientBar[0].getBoundingClientRect().height
				: 0;

			const clientHeader = document.getElementsByClassName('header-new');
			const clientHeaderHeight = clientHeader.length
				? clientHeader[0].getBoundingClientRect().height
				: 0;

			const section = document.getElementById(sectionId);
			const scrollToSpot =
				section &&
				section.offsetTop -
				topHeaderHeight +
				clientBarHeight +
				clientHeaderHeight +
				3;

			const XaxisZero = 0;
			if (
				/*cc_on!@*/ !!document.documentMode ||
				Object.prototype.toString
					.call(window.HTMLElement)
					.indexOf('Constructor') > 0 ||
				window.navigator.userAgent.indexOf('Edge') > -1
			) {
				//checks for internet explorer or safari
				window.scrollTo(XaxisZero, scrollToSpot);
			} else {
				window.scrollTo({
					top: scrollToSpot,
					left: XaxisZero,
					behavior: scrollBehavior || 'smooth'
				});
			}
		},
		stickyScroll() {
			if (!this.stickySupported) {
				const header = document.getElementById('top-header');
				const isFixed = header.classList.contains('fixed-header');
				const stickPoint = 111; //The height of the CG header - the blue bar becomes fixed above 111px of scroll
				const offset = window.pageYOffset;

				if (offset > stickPoint && !isFixed) {
					header.classList.add('fixed-header');
					header.parentElement.style.paddingTop = '190px';
				} else if (offset <= stickPoint && isFixed) {
					header.classList.remove('fixed-header');
					header.parentElement.style.paddingTop = '0';
				}
			}

			const topHeader = document.getElementById('top-header');
			const topHeaderHeight =
				topHeader &&
				topHeader.getBoundingClientRect() &&
				topHeader.getBoundingClientRect().height;
			let sectionInView =
				this.navSections[0] && this.navSections[0].label;

			for (const section of this.navSections) {
				const sectionElement = document.getElementById(
					section.elementId
				);
				const sectionPosition =
					sectionElement &&
					sectionElement.getBoundingClientRect() &&
					sectionElement.getBoundingClientRect().top -
					topHeaderHeight;
				if (
					sectionPosition <= 2 &&
					sectionPosition !== null && sectionPosition !== undefined
				) {
					sectionInView = section.label;
				}
			}
			this.updateSelection(sectionInView, false);
		}
	}
};
</script>

<style lang="less">
@import '../../assets/css/variables.less';

#top-header {
	width: 100%;
	position: sticky;
	top: 0;
	z-index: 20;
	background-color: #f9f9f9;

	&.scrollable {
		position: initial;
	}

	&.fixed-header {
		position: fixed;
	}

	.breadcrumb-path-link {
		max-width: 996px;
		padding-right: 13px;
		padding-left: 13px;
		margin-left: auto;
		margin-right: auto;
		padding-bottom: 6px;

		a {
			color: #1d5799;
			font-family: @avenir-demi-sans;
			text-decoration: none;
			font-size: 13px;
		}

		span.fund-title {
			font-family: @avenir-regular;
			color: @dark-gray;
			font-size: 13px;
		}
	}

	#page-banner {
		display: flex;
		color: #fff;
		height: auto;
		min-height: 90px;
		background-position: center top;
		background-size: auto 100%;
		background-image: url('data:image/jpg;base64,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');

		.title-container {
			min-height: 90px;
			width: 996px;
			padding-right: 13px;
			padding-left: 13px;
			margin-left: auto;
			margin-right: auto;
			display: flex;

			.title {
				display: flex;
				flex-direction: column;
				flex: 1;
				margin-right: auto;

				>div {
					color: white;
					font-family: 'Avenir Next LT W01 Demi', Arial;
					font-size: 12px;
					margin-bottom: 17px;
				}

				h2 {
					font-size: 24px;
					font-family: 'Avenir Next LT W01 Demi', Arial;
					color: #fff;
					margin: 0;
					margin-top: auto;
					margin-bottom: 30px;

					p {
						display: inline;

						&:nth-of-type(2) {
							font-size: 20px;
						}
					}
				}
			}

			button {
				background-color: #009adf;
				background-image: linear-gradient(180deg, #05aaf3, #009adf);
				border: 0;
				color: #fff;
				font-family: Avenir Next LT W01 Demi, Arial, sans-serif;
				font-size: 14px;
				padding: 8px;
				height: 36px;
				min-width: max-content;
				margin: auto 0;
				margin-left: 15px;
				white-space: nowrap;

				&:hover {
					background-color: #66c2ec;
					background-image: linear-gradient(180deg, #66c2ec, #66c2ec);
					text-decoration: underline;
				}
			}
		}
	}

	#toolbar-wrapper {
		width: 100%;

		#tool-bar {
			width: 100%;
			max-width: 996px;
			margin: auto;
			padding: 13px;
			display: flex;

			.share-class-dropdown {
				min-width: fit-content;
				max-height: fit-content;
				margin-right: auto;

				.table-label {
					color: @gray;
					display: inline-block;
					font-family: AvenirNextLTW01-Regular, Arial;
					font-size: 14px;
					line-height: 24px;
					margin: 0;
					margin-right: 5px;
					padding: 0;

					&.vehicle {
						margin-right: 3px;
					}

					&.vehicle-value {
						font-weight: 700;
					}
				}
			}

			.icon {
				margin: 0 10px;
				line-height: 24px;
				color: #1d5799;
			}

			.item {
				line-height: 24px;
				font-size: 12px;
				font-family: Avenir Next LT W01 Demi, Arial, sans-serif;
				border: none;
				background-color: transparent;
				color: #1d5799;

				&:hover {
					text-decoration: underline;
				}
			}
		}
	}

	#nav-wrapper {
		width: 100%;
		.module-border-top();
		.thin-light-gray-border-bottom();

		#nav-bar {
			width: 100%;
			max-width: 996px;
			margin: auto;
			padding: 0 13px;
			display: flex;

			button {
				flex-grow: 1;
				border: none;
				text-decoration: none;
				line-height: 33px;
				font-family: Avenir Next LT W01 Demi, Arial, sans-serif;
				font-size: 12px;
				background-color: transparent;
				text-transform: uppercase;
				white-space: nowrap;
				color: #1d5799;
				padding: 0;

				&.active {
					background-color: #009adf;
					color: #ffffff;
				}

				&:hover {
					background-color: @light-gray;
				}
			}
		}
	}

	#nav-dropdown {
		display: none;
		border-bottom: 3px solid #666;

		>div {
			font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
			font-size: 18px;
			text-transform: uppercase;
			line-height: 36px;
			color: #fff;
			padding: 0 13px;
			background-image: linear-gradient(180deg, #999, gray);

			&.selected {
				background-image: linear-gradient(180deg, #33aee5, #009adf);

				.nav-dropdown-arrow {
					width: 0;
					height: 0;
					border-style: solid;
					margin: 12px 0;
					display: block;
					float: right;

					&.up {
						border-color: transparent transparent #ffffff transparent;
						border-width: 0 7.5px 9px 7.5px;
					}

					&.down {
						border-width: 9px 7.5px 0 7.5px;
						border-color: #ffffff transparent transparent transparent;
					}
				}
			}
		}
	}
}

@media (max-width: 768px) {
	#top-header {
		position: initial;

		#toolbar-wrapper #tool-bar .item.print {
			//hide PRINT button @ tablet
			display: none;
		}

		#nav-wrapper {
			display: none;
		}

		#nav-dropdown {
			display: block;
		}

		#page-banner {
			.title-container {
				flex-wrap: wrap;

				.title {
					flex-basis: 100%;

					div {
						margin-bottom: 10px;
					}

					h2 {
						margin-bottom: 10px;

						&.bottom-spacer {
							margin-bottom: 30px;
						}
					}
				}

				button {
					margin-left: 0px;
					margin-bottom: 15px;
				}
			}
		}
	}
}

@media (max-width: 480px) {
	#top-header {
		#page-banner {
			.title-container {
				.title {
					div {
						margin-bottom: 17px;
					}

					h2 {
						margin-bottom: 30px;
					}
				}

				button {
					//hide PRINT & DOWNLOAD buttons @ mobile
					display: none;
				}
			}
		}
	}
}
</style>
